import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PATHS } from "../../routes/paths";
import { listCidadesAdmin } from "../../services/cidades";
import api from "../../services/api";
import "./MenuAdmin.css";

function FlowerIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 250 200"
      width="70%"
      height="70%"
      {...props}
    >
      <defs>
        <path
          id="petal-path"
          d="M 0 8 C -14 28, -23 45, -23 60 A 23 23 0 1 0 23 60 C 23 45, 14 28, 0 8 Z"
        />
      </defs>

      <g transform="translate(125, 100)">
        <use href="#petal-path" fill="#ffffff" transform="rotate(51.428)" />
        <use href="#petal-path" fill="#ffffff" transform="rotate(102.857)" />
        <use href="#petal-path" fill="#ffffff" transform="rotate(154.286)" />
        <use href="#petal-path" fill="#ffffff" transform="rotate(205.714)" />
        <use href="#petal-path" fill="#ffffff" transform="rotate(257.143)" />
        <use href="#petal-path" fill="#ffffff" transform="rotate(308.571)" />
        <use href="#petal-path" fill="#00a0e3" transform="rotate(0)" />
      </g>
    </svg>
  );
}

function ChevronIcon({ size = 10, color = "#ffffff", open }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 8"
      fill="none"
      className={`admin-sidebar-chevron${open ? " open" : ""}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 1.5L6 6.5L11 1.5"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LogoutIcon({ size = 16, color = "#ffffff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 21H5C3.9 21 3 20.1 3 19V5C3 3.9 3.9 3 5 3H9"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 17L21 12L16 7"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 12H9"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GlobeIcon({ size = 16, color = "#ffffff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8" />
      <path d="M3 12H21" stroke={color} strokeWidth="1.8" />
      <path
        d="M12 3C14.5 5.7 15.8 8.8 15.8 12C15.8 15.2 14.5 18.3 12 21C9.5 18.3 8.2 15.2 8.2 12C8.2 8.8 9.5 5.7 12 3Z"
        stroke={color}
        strokeWidth="1.8"
      />
    </svg>
  );
}

function MenuIcon({ size = 26, color = "#1c4e9c" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M3 6H21" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
      <path d="M3 12H21" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
      <path d="M3 18H21" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon({ size = 22, color = "#ffffff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 5L19 19"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d="M19 5L5 19"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const NAV_ITEMS = [
  { label: "Home", to: `${PATHS.adminHome}` },
  { label: "Quem Somos", to: `/admin${PATHS.quemSomos}` },
  { label: "Metodologia", to: `/admin${PATHS.metodologia}` },
  {
    label: "Onde Estamos",
    to: PATHS.adminCidades,
    expandable: true,
    dynamicChildren: "cidades",
  },
  { label: "Para Empresas", to: PATHS.adminParaEmpresas },
  { label: "Blog", to: PATHS.adminBlog },
  { label: "Transparência", to: PATHS.adminTransparencia },
  { label: "Contato", to: PATHS.adminContato },
  { label: "Apoie", to: PATHS.adminApoie },
  { label: "Rodapé", to: PATHS.adminRodape },
  { label: "Cabeçalho", to: PATHS.adminCabecalho },
];

export default function MenuAdmin() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(() =>
    pathname.startsWith(PATHS.adminCidades),
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cidades, setCidades] = useState([]);

  useEffect(() => {
    listCidadesAdmin()
      .then((data) => setCidades(Array.isArray(data) ? data : []))
      .catch(() => setCidades([]));
  }, []);

  const isItemActive = (item) =>
    pathname === item.to ||
    pathname.startsWith(`${item.to}/`) ||
    item.children?.some((child) => pathname === child.to);

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleClick = (item) => {
    if (item.expandable) {
      if (pathname !== item.to && !pathname.startsWith(`${item.to}/`)) {
        handleNavigate(item.to);
      }
      setExpanded((previous) => !previous);
      return;
    }

    handleNavigate(item.to);
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      document.cookie = "token=; Max-Age=0; path=/";
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="admin-shell">
      {!mobileOpen && (
        <button
          className="admin-sidebar-menu-button"
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menu"
        >
          <MenuIcon />
        </button>
      )}

      {mobileOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside className={`admin-sidebar${mobileOpen ? " open" : ""}`}>
        <button
          className="admin-sidebar-close-button"
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
        >
          <CloseIcon />
        </button>

        <div className="admin-sidebar-logo">
          <FlowerIcon size={44} />
          <div className="admin-sidebar-logo-text">7GERAÇÕES</div>
        </div>

        <div className="admin-sidebar-section-label">Seções</div>

        <nav className="admin-sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <div key={item.label}>
              <button
                onClick={() => handleClick(item)}
                className={`admin-sidebar-item${
                  isItemActive(item) ? " active" : ""
                }`}
              >
                <span>{item.label}</span>
                {item.expandable && <ChevronIcon open={expanded} />}
              </button>

              {item.expandable && expanded && (
                <div className="admin-sidebar-submenu">
                  {item.dynamicChildren === "cidades" ? (
                    <>
                      <button
                        className={`admin-sidebar-subitem${
                          pathname === PATHS.adminCidades ? " active" : ""
                        }`}
                        onClick={() => handleNavigate(PATHS.adminCidades)}
                      >
                        Todas as cidades
                      </button>

                      {cidades.length === 0 ? (
                        <span className="admin-sidebar-subitem-empty">
                          Nenhuma cidade cadastrada
                        </span>
                      ) : (
                        cidades.map((cidade) => {
                          const cidadePath = PATHS.adminCidade.replace(
                            ":slug",
                            cidade.slug,
                          );

                          return (
                            <button
                              key={cidade._id || cidade.slug}
                              className={`admin-sidebar-subitem${
                                pathname === cidadePath ? " active" : ""
                              }`}
                              onClick={() => handleNavigate(cidadePath)}
                            >
                              {cidade.city || cidade.name}
                            </button>
                          );
                        })
                      )}
                    </>
                  ) : (
                    item.children?.map((child) => (
                      <button
                        key={child.label}
                        className="admin-sidebar-subitem"
                        onClick={() => handleNavigate(child.to)}
                      >
                        {child.label}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <button
            className="admin-sidebar-footer-button"
            onClick={handleLogout}
          >
            <LogoutIcon />
            Logout
          </button>

          <button
            className="admin-sidebar-footer-button"
            onClick={() => handleNavigate(PATHS.home)}
          >
            <GlobeIcon />
            <span>Ir para o site</span>
          </button>
        </div>
      </aside>

      <main className="admin-shell__content">
        <Outlet />
      </main>
    </div>
  );
}